import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { subscribeToAlert } from '../../../../redux/actions'

// MaterialUI
import {
  Grid, Typography, Button, Tooltip,
  Accordion, AccordionDetails, AccordionSummary,
  List, ListItem, ListItemText, IconButton
} from '@material-ui/core'

// Icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import LibraryAddIcon from '@material-ui/icons/LibraryAdd'
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck'
import { OUAlertSelectionModal } from '../../modals'

// This Component refelcts all units the user has permission to see. They can delete and edit based on the permissions of the user and whether the author has made the alert Public
const OrgAlerts = (props) => {
  const dispatch = useDispatch()
  const {
    classes, unitAlertInfo, GreenCheckbox, truncateText, handleDelete, formDataSet, TreeData,
    selectedKeys, fireSubscribeFailure, fireSubscribeSuccess, setEditing, unSubscribeAlert, noSMS, formCloneSwap
  } = props

  // This pulls in the supervisors in order to match the alert author's supervisorID with a readble first name and last name for display
  const orgSupervisors = useSelector(state => state.orgContacts.availableContacts)
  const { permissions: Permissions, supervisorID: User } = useSelector(state => state.auth)

  // This gets all the alerts from both the organizational view and the author generated alerts
  const totalOrgList = unitAlertInfo.unit.concat(unitAlertInfo.supervisor)

  // Opens the OU modal
  const [openUnits, setOpenUnits] = useState(false)

  // State items to populate error information
  const [noUnit, setNoUnit] = useState(false)
  const [noDeliveryMethod, setNoDeliveryMethod] = useState(false)
  const [canEditAlert, setCanEditAlert] = useState(false)

  const [supervisorList, setSupervisorList] = useState([])

  // Temporary object holding the information for the OU modal to dispatch
  const baseUnitObject = {
    emailNotificationID: '',
    selectedUnits: [],
    sendSMS: false,
    sendEmail: false,
    isSubscribed: false
  }

  const [newUnitAlerts, setNewUnitAlerts] = useState(baseUnitObject)
  // Storing alert selected for unit adding/deleting logic
  const [targetAlert, setTargetAlert] = useState({})

  useMemo(() => {
    if (Permissions && Array.isArray(Permissions)) {
      setCanEditAlert(Permissions.findIndex((permissionKey) => permissionKey === 'alert-editor') !== -1)
    }
  }, [Permissions])

  // Passes in the alert information to the OU modal
  const handleUnitOpen = (alertInfo) => {
    setNewUnitAlerts({
      ...newUnitAlerts,
      emailNotificationID: alertInfo.emailNotificationID,
      selectedUnits: alertInfo.subscribed.units.map((unitInfo) => unitInfo.unitID),
      sendSMS: alertInfo.subscribed.sendSMS,
      sendEmail: alertInfo.subscribed.sendEmail,
      isSubscribed: alertInfo.subscribed.isSubscribed
    })
    setTargetAlert({ ...alertInfo.subscribed })
    setOpenUnits(true)
  }

  // The cancel button handler in the OU modal
  const handleOUCancel = () => {
    setOpenUnits(false)
    setTargetAlert({})
    setNoDeliveryMethod(false)
    setNoUnit(false)
    setNewUnitAlerts(baseUnitObject)
  }

  // Handles the ok button in the OU modal
  const handleOUOk = () => {
    // !!TODO: this may need to handle diffrently if the org does not have SMS enabled
    if (!newUnitAlerts.selectedUnits.length && newUnitAlerts.isSubscribed) {
      setNoDeliveryMethod(false)
      setNoUnit(true)
    } else if (newUnitAlerts.selectedUnits.length && !newUnitAlerts.isSubscribed) {
      setNoUnit(false)
      setNoDeliveryMethod(true)
    } else {
      const [unitID] = selectedKeys
      const unitsToDelete = []

      targetAlert.units.forEach((targetUnitInfo) => {
        if (newUnitAlerts.selectedUnits.findIndex((selectedUnit) => selectedUnit === targetUnitInfo.unitID) === -1) {
          unitsToDelete.push(targetUnitInfo.unitID)
        }
      })

      const subscription = {
        emailNotificationID: newUnitAlerts.emailNotificationID,
        unitsToAdd: [...newUnitAlerts.selectedUnits],
        unitsToDelete: [...unitsToDelete],
        sendSMS: newUnitAlerts.sendSMS,
        sendEmail: newUnitAlerts.sendEmail,
        isSubscribed: newUnitAlerts.isSubscribed
      }
      dispatch(subscribeToAlert(unitID, subscription, handleSubscribeSuccess, fireSubscribeFailure))
    }
  }

  const handleSubscribeSuccess = () => {
    fireSubscribeSuccess()
    setNewUnitAlerts(baseUnitObject)
    setTargetAlert({})
    setOpenUnits(false)
  }

  useEffect(() => {
    // on componant load this will further refine the suprvisors into a concise object
    if (orgSupervisors) {
      if (orgSupervisors.length !== 0) {
        const tempList = []
        orgSupervisors.forEach((supervisor) => {
          tempList.push({ supervisorID: supervisor.supervisorID, firstName: supervisor.firstName, lastName: supervisor.lastName })
        })
        setSupervisorList(tempList)
      }
    }
  }, [orgSupervisors])

  // This will check to see if the alert has been subscribed to and if it has had a OU chosen for it or not and render the correct icon
  const checkOUStatus = (emailNotificationID) => {
    if (newUnitAlerts) {
      // Checks for the alert in the org alerts
      const alertInfo = totalOrgList.find((orgInfo) => orgInfo.emailNotificationID === emailNotificationID)
      // Checks to see if the alert is in the in the unit array, meaning they have selected this alert previosuly
      if (alertInfo) {
        if (alertInfo.subscribed.isSubscribed === true) {
          return (
            <IconButton className={classes.unsubscribeBtn} onClick={() => { handleUnitOpen(alertInfo) }}>
              <PlaylistAddCheckIcon size='small' />
            </IconButton>
          )
        } else {
          return (
            <IconButton className={classes.subscribeBtn} onClick={() => { handleUnitOpen(alertInfo) }}>
              <LibraryAddIcon size='small' />
            </IconButton>
          )
        }
      }
    }
  }

  // Uses the supervisorID supplied from the alert object to find the readable name to display
  const findAuthorName = (supervisor) => {
    if (supervisorList && supervisorList.length !== 0) {
      const index = supervisorList.findIndex(x => x.supervisorID === supervisor)
      return supervisorList[index] ? `${supervisorList[index]?.firstName} ${supervisorList[index]?.lastName}` : 'User Unavailable'
    }
  }

  const handleEdit = (unit) => {
    if (unit.clonedAlert !== 1) {
      formDataSet(unit)
      setEditing(true)
    } else {
      formCloneSwap(unit.emailNotificationID)
      setEditing(true)
    }
  }

  return (
    <>
      {/* This is the OU modal */}
      <OUAlertSelectionModal
        openUnits={openUnits}
        handleOUCancel={handleOUCancel}
        handleOUOk={handleOUOk}
        classes={classes}
        GreenCheckbox={GreenCheckbox}
        newUnitAlerts={newUnitAlerts}
        setNewUnitAlerts={setNewUnitAlerts}
        noSMS={noSMS}
        TreeData={TreeData}
        noUnit={noUnit}
        noDeliveryMethod={noDeliveryMethod}
      />

      <Grid container justify='center' className={classes.accordionroot}>
        <Grid item xs={12}>
          {/* Maps through a concatenated version of both the organizational alerts and the user generated alerts */}
          {
            totalOrgList && totalOrgList.map((unit) => {
              const {
                emailNotificationID, emailNotificationName = '', emailNotificationDesc = '', unitID: emailUnitID = '',
                privateAlert = false, author, subscribed = {}, clonedAlert = false, type: alertType = 'S',
                categories: unitCategories = [], words: unitWords = [], workstations: unitWorkstations = [], users: unitUsers = []
              } = unit

              const isOrgAlert = privateAlert === false

              return (
                !privateAlert && (
                  <Accordion key={`alert-number${emailNotificationID}`} elevation={3}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-label='Expand'
                      className={classes.AccSumm}
                    >
                      <Grid container direction='row' alignItems='center' justify='space-between' wrap='nowrap'>
                        <Grid item>
                          <Grid container direction='row' alignItems='center' wrap='nowrap'>
                            <Grid item>
                              <Typography className={classes.heading} style={{ color: !subscribed.isSubscribed ? '#5291fc' : '#A4D164' }}>Title:</Typography>
                            </Grid>

                            <Grid item>
                              <Tooltip title={emailNotificationName} placement='top' arrow enterDelay={500} enterNextDelay={500}>
                                <Typography className='ml-1' style={{ textTransform: 'none' }}>{truncateText(emailNotificationName)}</Typography>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item>
                          <Grid container direction='row' wrap='nowrap' spacing={1} className={classes.fixSpacing}>
                            <Grid item>
                              <Grid container direction='row'>
                                <Grid container item direction='column' alignItems='center'>
                                  <Grid item>
                                    <Typography variant='caption' style={{ textTransform: 'none', textDecoration: 'underline' }}>
                                      Subscribe
                                    </Typography>
                                  </Grid>

                                  <Grid item>
                                    {checkOUStatus(emailNotificationID)}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item>
                              {/* This Icon will only appear if the user has permissions AND if the alert is public, OR is they are the author */}
                              {
                                author === User || (canEditAlert && !privateAlert)
                                  ? (
                                    <Tooltip title='Edit'>
                                      <IconButton onClick={() => { handleEdit(unit) }} className={classes.alertBtn}>
                                        <EditIcon size='small' />
                                      </IconButton>
                                    </Tooltip>)
                                  : (
                                    <></>)
                              }
                            </Grid>

                            <Grid item>
                              {
                                canEditAlert || author === User
                                  ? (
                                    <Tooltip title='Delete'>
                                      <IconButton
                                        onClick={() => { handleDelete(emailUnitID, emailNotificationID, isOrgAlert.toString()) }}
                                        className={classes.alertBtn}
                                        size='small'
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>)
                                  : (<></>)
                              }
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionSummary>

                    <AccordionDetails className={classes.accDeets}>
                      <Grid container direction='row'>
                        <Grid container item direction='column' xs={12} lg={8}>

                          <Grid container item direction='row'>
                            <Grid item>
                              <Typography variant='subtitle1' style={{ textTransform: 'none', fontWeight: 'bold' }}>
                                Alert Details:
                              </Typography>
                            </Grid>
                          </Grid>

                          <Grid container item direction='row'>
                            <Grid item>
                              {
                                Boolean(clonedAlert) && (
                                  <Typography variant='subtitle2' style={{ textTransform: 'none' }}>
                                    LearnSafe Preferred Customized Alert
                                  </Typography>
                                )
                              }
                            </Grid>
                          </Grid>

                          <Grid container item direction='row'>
                            <Grid item>
                              <Typography variant='caption' gutterBottom className={classes.secondaryHeading}>
                                Author: {findAuthorName(author)}
                              </Typography>
                            </Grid>
                          </Grid>

                          {
                            alertType === 'S' && (
                              <Grid container item direction='row'>
                                <Grid container item direction='column'>
                                  <Grid item>
                                    <Typography variant='caption' style={{ textTransform: 'none', fontWeight: 'bold', color: '#5291fc' }}>
                                      Severity:
                                    </Typography>
                                  </Grid>

                                  <Grid item>
                                    <Typography variant='caption' style={{ textTransform: 'none' }}>
                                      {`${unit.minSeverity} - ${unit.maxSeverity}`}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            )
                          }

                          <Grid container item direction='row'>
                            <Grid container item direction='column'>
                              <Grid item>
                                <Typography variant='caption' style={{ textTransform: 'none', fontWeight: 'bold', color: '#5291fc' }}>
                                  Description:
                                </Typography>
                              </Grid>

                              <Grid item>
                                <Typography variant='caption' gutterBottom style={{ textTransform: 'none' }}>
                                  {emailNotificationDesc}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid container item direction='row' justify='space-between'>
                            <Grid item>
                              <Grid container direction='row'>
                                <Grid container item direction='column'>
                                  <Grid item>
                                    <Typography variant='caption' style={{ textTransform: 'none', fontWeight: 'bold', color: '#5291fc' }}>
                                      Category:
                                    </Typography>
                                  </Grid>

                                  <Grid item>
                                    {
                                      unitCategories && Boolean(unitCategories.length)
                                        ? (
                                          <List dense className={classes.listRoot}>
                                            {
                                              unitCategories.map((category, i) => {
                                                return (
                                                  <ListItem
                                                    key={`${emailNotificationID}-category-number-${i}-${category}`}
                                                    className={classes.listItem}
                                                  >
                                                    <ListItemText
                                                      className={classes.listItemText}
                                                      primary={
                                                        <Typography variant='caption' style={{ textTransform: 'capitalize' }}>
                                                          {category}
                                                        </Typography>
                                                      }
                                                    />
                                                  </ListItem>
                                                )
                                              })
                                            }
                                          </List>)
                                        : (
                                          <Typography variant='caption' gutterBottom style={{ textTransform: 'none' }}>
                                            N/A
                                          </Typography>)
                                    }
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>

                            {
                              !clonedAlert && (
                                <Grid item>
                                  <Grid container direction='row'>
                                    <Grid container item direction='column'>
                                      <Grid item>
                                        <Typography variant='caption' style={{ textTransform: 'none', fontWeight: 'bold', color: '#5291fc' }}>
                                          Phrases:
                                        </Typography>
                                      </Grid>

                                      <Grid item>
                                        {
                                          unitWords && Boolean(unitWords.length)
                                            ? (
                                              <List dense className={classes.listRoot}>
                                                {
                                                  unitWords.map((word, i) => {
                                                    return (
                                                      <ListItem
                                                        key={`${emailNotificationID}-word-number-${i}-${word}`}
                                                        className={classes.listItem}
                                                      >
                                                        <ListItemText
                                                          className={classes.listItemText}
                                                          primary={
                                                            <Typography variant='caption' style={{ textTransform: 'capitalize' }}>
                                                              {word}
                                                            </Typography>
                                                          }
                                                        />
                                                      </ListItem>
                                                    )
                                                  })
                                                }
                                              </List>)
                                            : (
                                              <Typography variant='caption' gutterBottom style={{ textTransform: 'none' }}>
                                                N/A
                                              </Typography>)
                                        }
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )
                            }

                            <Grid item>
                              <Grid container direction='row'>
                                <Grid container item direction='column'>
                                  <Grid item>
                                    <Typography variant='caption' style={{ textTransform: 'none', fontWeight: 'bold', color: '#5291fc' }}>
                                      Workstations:
                                    </Typography>
                                  </Grid>

                                  <Grid item>
                                    {
                                      unitWorkstations && Boolean(unitWorkstations.length)
                                        ? (
                                          <List dense className={classes.listRoot}>
                                            {
                                              unitWorkstations.map((workstation, i) => {
                                                return (
                                                  <ListItem
                                                    key={`${emailNotificationID}-workstation-number-${i}-${workstation}`}
                                                    className={classes.listItem}
                                                  >
                                                    <ListItemText
                                                      className={classes.listItemText}
                                                      primary={
                                                        <Typography variant='caption' style={{ textTransform: 'capitalize' }}>
                                                          {workstation}
                                                        </Typography>
                                                      }
                                                    />
                                                  </ListItem>
                                                )
                                              })
                                            }
                                          </List>)
                                        : (
                                          <Typography variant='caption' gutterBottom style={{ textTransform: 'none' }}>
                                            N/A
                                          </Typography>)
                                    }
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid container item direction='row' justify='space-between'>
                            <Grid item>
                              <Grid container direction='row'>
                                <Grid container item direction='column'>
                                  <Grid item>
                                    <Typography variant='caption' style={{ textTransform: 'none', fontWeight: 'bold', color: '#5291fc' }}>
                                      User:
                                    </Typography>
                                  </Grid>

                                  <Grid item>
                                    {
                                      unitUsers && Boolean(unitUsers.length)
                                        ? (
                                          <List dense className={classes.listRoot}>
                                            {
                                              unitUsers.map((user, i) => {
                                                return (
                                                  <ListItem
                                                    key={`${emailNotificationID}-user-number-${i}-${user}`}
                                                    className={classes.listItem}
                                                  >
                                                    <ListItemText
                                                      className={classes.listItemText}
                                                      primary={
                                                        <Typography variant='caption' style={{ textTransform: 'capitalize' }}>
                                                          {user}
                                                        </Typography>
                                                      }
                                                    />
                                                  </ListItem>
                                                )
                                              })
                                            }
                                          </List>)
                                        : (
                                          <Typography variant='caption' gutterBottom style={{ textTransform: 'none' }}>
                                            N/A
                                          </Typography>)
                                    }
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          {
                            Boolean(subscribed.isSubscribed) && (
                              <Grid container item direction='row'>
                                <Grid item>
                                  <Button
                                    variant='contained'
                                    size='small'
                                    className={classes.greenBtn}
                                    onClick={() => { unSubscribeAlert(unit.emailNotificationID) }}
                                  >
                                    Unsubscribe
                                  </Button>
                                </Grid>
                              </Grid>
                            )
                          }
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>)
              )
            })
          }
        </Grid>
      </Grid>
    </>
  )
}

export default OrgAlerts
